/**
 * `Segmented` — segmented control for coach-facing filters and toggles.
 *
 * Filters and unit toggles on coach pages are ALWAYS segmented controls, never
 * `<select>` dropdowns: a dropdown costs two taps and hides the current value
 * behind a popover on a sideline tablet. Every segment meets the 44 px WCAG
 * 2.5.5 target floor.
 *
 * ARIA: `role="tablist"` + `role="tab"` (the default) for view/filter switches,
 * so assistive tech announces "tab 2 of 4" and arrow keys move between
 * segments. `role="group"` + `aria-pressed` is available for controls that are
 * not a view switch (e.g. sort order).
 */
import { useRef, type KeyboardEvent } from 'react';

export interface SegmentedOption<TValue extends string> {
  value: TValue;
  label: string;
  /** Narrow-viewport label; falls back to `label`. */
  shortLabel?: string;
  /** Trailing count/badge, e.g. a filtered row total. */
  badge?: string | number;
  isDisabled?: boolean;
}

export interface SegmentedProps<TValue extends string> {
  /** Accessible name for the whole control. */
  label: string;
  options: readonly SegmentedOption<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  /** `tabs` (default) emits tablist/tab; `group` emits aria-pressed buttons. */
  semantics?: 'tabs' | 'group';
  className?: string;
  testId?: string;
}

const ARROW_KEYS: Record<string, number> = {
  ArrowLeft: -1,
  ArrowUp: -1,
  ArrowRight: 1,
  ArrowDown: 1,
};

export function Segmented<TValue extends string>({
  label,
  options,
  value,
  onChange,
  semantics = 'tabs',
  className,
  testId,
}: SegmentedProps<TValue>): JSX.Element {
  const listRef = useRef<HTMLDivElement>(null);
  const isTabs = semantics === 'tabs';

  function onKeyDown(keyEvent: KeyboardEvent<HTMLDivElement>): void {
    const step = ARROW_KEYS[keyEvent.key];
    if (step === undefined || !isTabs) return;
    const enabled = options.filter((option) => option.isDisabled !== true);
    if (enabled.length === 0) return;
    const current = Math.max(
      0,
      enabled.findIndex((option) => option.value === value)
    );
    const next = enabled[(current + step + enabled.length) % enabled.length];
    keyEvent.preventDefault();
    onChange(next.value);
    const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('button');
    buttons?.[options.indexOf(next)]?.focus();
  }

  const classes = ['segmented', className ?? ''].filter((entry) => entry !== '').join(' ');

  return (
    <div
      ref={listRef}
      className={classes}
      role={isTabs ? 'tablist' : 'group'}
      aria-label={label}
      aria-orientation={isTabs ? 'horizontal' : undefined}
      data-testid={testId}
      onKeyDown={onKeyDown}
    >
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            className={`segmented__segment${isActive ? ' is-active' : ''}`}
            role={isTabs ? 'tab' : undefined}
            aria-selected={isTabs ? isActive : undefined}
            aria-pressed={isTabs ? undefined : isActive}
            tabIndex={isTabs && !isActive ? -1 : 0}
            disabled={option.isDisabled === true}
            onClick={() => onChange(option.value)}
          >
            <span className="segmented__label">{option.label}</span>
            {option.shortLabel === undefined ? null : (
              <span className="segmented__label-short" aria-hidden="true">
                {option.shortLabel}
              </span>
            )}
            {option.badge === undefined ? null : (
              <span className="segmented__badge num">{option.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
